import { createTheme } from "@mui/material/styles";
import type { ThemeOptions, Theme } from "@mui/material/styles";
import type { CSSProperties } from "react";
import { BASE_APP_LAYOUT, BASE_THEME_OPTIONS } from "./baseTheme";
import { lightTheme } from "./themes/light";
import { darkTheme } from "./themes/dark";

/** Header nav tab colors — used only for NAV_LINKS tabs in AppHeader. */
export interface HeaderNavLinksPalette {
  textColor: string;
}

/** DataGrid (e.g. bonds table) — color tokens only; sizes live in BondDataGrid. */
export interface DataGridPalette {
  secondaryTextColor: string;
  headerTextColor: string;
  headerBackgroundColor: string;
  /** Column-header band fill in the bond and message grids. */
  headerSurfaceColor: string;
  /** Hairline under the column-header band. */
  headerRuleColor: string;
  rowBorderColor: string;
  /** Grid footer / pagination bar fill. */
  footerBackgroundColor: string;
  /** 1px rule above the grid footer / pagination bar. */
  footerBorderColor: string;
  /** Unchecked selection-checkbox border / glyph color. */
  selectionCheckboxColor: string;
  /** Column header title + sort icon when that column is actively sorted. */
  headerSortedTextColor: string;
  /** Default body-cell text (issuer, coupon, dates, yields, …). */
  cellTextColor: string;
  /** CUSIP link text in the bond grid. */
  cusipLinkColor: string;
  /**
   * Left accent border color for the row currently open in the detail drawer.
   * Optional so custom themes (API-loaded) can omit it and fall back to the
   * palette primary in the grid.
   */
  activeRowBorderColor?: string;
  /**
   * Background tint for the row currently open in the detail drawer.
   * Optional for the same reason as `activeRowBorderColor`.
   */
  activeRowBackgroundColor?: string;
  /** Stacked-metric column backgrounds on the Watchlist page (Offers/Bids/RFQs/Trades). */
  watchlistMetrics: WatchlistMetricPalette;
  /** Full-bleed fill for the watchlist metric column headers. */
  watchlistMetricHeaders: WatchlistMetricPalette;
  /** Stacked-metric text on the Watchlist page; `count` also colors the column header. */
  watchlistMetricInk: WatchlistMetricInkPalette;
  /** Bond-type and feature tags in the Type column. */
  bondTypeChip: BondTypeChipPalette;
}

/** Fill and ink for a filled bond-type tag in the Type column. */
export interface BondTypeFill {
  bg: string;
  fg: string;
}

/** Border and ink for an outlined feature tag in the Type column. */
export interface BondFeatureOutline {
  border: string;
  fg: string;
}

/** Type-column tag colors. Sizes live on the chip components. */
export interface BondTypeChipPalette {
  /** GO family and Certificates of Participation. */
  go: BondTypeFill;
  revenue: BondTypeFill;
  /** Italic Unclassified label. Not a filled chip. */
  unclassified: string;
  /** Outlined feature tags, and unknown bond-type codes. */
  feature: BondFeatureOutline;
}

/** Bold count and small quantity inks for one watchlist metric column. */
export interface WatchlistMetricInk {
  count: string;
  quantity: string;
}

export interface WatchlistMetricInkPalette {
  offers: WatchlistMetricInk;
  bids: WatchlistMetricInk;
  rfqs: WatchlistMetricInk;
  trades: WatchlistMetricInk;
}

/** Watchlist stacked-metric column backgrounds (Bids/Offers/RFQs/Trades). */
export interface WatchlistMetricPalette {
  offers: string;
  bids: string;
  rfqs: string;
  trades: string;
}

/** Filter bar — backgrounds distinct from default paper (e.g. data endpoint tab switcher). */
export interface FilterBarPalette {
  /** Background for RatingsMin / Rating Select triggers (shared filterSelectSx). */
  endpointSwitcherBackground: string;
  /** Background for the Markets / RFQs / Messages tab switcher Select. */
  tabSwitcherBackground: string;
  /** Border color for the tab switcher Select. */
  tabSwitcherBorderColor: string;
  /** Label text color for the tab switcher Select. */
  tabSwitcherTextColor: string;
  /** Dropdown chevron color for the tab switcher Select. */
  tabSwitcherIconColor: string;
  /** External filter field labels (e.g. "States:", "Quantity:"). */
  barLabelColor: string;
  /** Text / icon color for outlined filter value triggers ("Any", summaries). */
  filterTriggerTextColor: string;
  /** Border color for outlined filter value triggers. */
  filterTriggerBorderColor: string;
  /** Text / icon color on the filled Advanced action button. */
  advancedButtonTextColor: string;
  /** Fill of contained filter actions (Advanced, popover Apply). Matches the active Munis pill. */
  filledActionBackgroundColor: string;
  /** Hover fill for the Clear text action. */
  clearButtonHoverBackground: string;
}

/** Header search input — color tokens (per-theme). The field sits on the dark header in both themes. */
export interface HeaderSearchColors {
  /** Placeholder text color (e.g. rgba(0,0,0,0.87) or hex). */
  placeholderColor: string;
  /** Icon and input text color on the header field. */
  iconAndTextColor: string;
  /** Border color for the resting outlined search. */
  borderColor: string;
  /** Background fill for the search container. */
  backgroundColor: string;
  /** Placeholder copy in the resting toolbar field. */
  restingPlaceholderColor: string;
  /** Typed value in the field. */
  filledTextColor: string;
  /** Focus border, caret, focused icon, and focus ring. */
  activeColor: string;
  /** Selected and hovered result row. */
  activeRowBackground: string;
  /** Open results card surface. */
  cardBackground: string;
  /** Input row of the open results card. */
  cardHeaderBackground: string;
  /** Search icon while the results card is open. */
  cardPlaceholderColor: string;
  /** Divider between result rows; also the result list scrollbar. */
  rowBorderColor: string;
  /** CUSIP column of a result row. */
  cusipColor: string;
  /** Issuer name column; also the clear button on hover. */
  nameColor: string;
  /** Bond type column. */
  typeColor: string;
  /** Coupon column. */
  couponColor: string;
  /** Maturity year column and status messages. */
  yearColor: string;
  /** Result count footer. */
  footerColor: string;
  /** Clear button at rest. */
  closeColor: string;
}

/** Header search input — sizes and animation (shared across themes, see baseTheme). */
export interface HeaderSearchLayout {
  /** Default width from sm breakpoint up (px). */
  widthPxSm: number;
  /** Width when focused from sm breakpoint up (px). */
  widthFocusedPxSm: number;
  /** Transition duration for width animation (ms). */
  transitionDurationMs: number;
}

/** Header search input — full runtime token set (colors + layout). */
export interface HeaderSearchPalette extends HeaderSearchColors, HeaderSearchLayout {}

/** App dialogs (AppModal) — paper border, header bar, and footer surface. */
export interface DialogPalette {
  borderColor: string;
  footerBackground: string;
  /** 1px hairline above the footer bar. */
  footerBorderColor: string;
  /** Hover fill for outlined footer actions (e.g. Close). */
  footerOutlinedHoverBackground: string;
  /** Drop shadow on the primary footer action (e.g. Save Current Search). */
  footerPrimaryButtonShadow: string;
  /** Fill of the primary footer action (e.g. Apply Filters). */
  footerPrimaryButtonBackground: string;
  /** Left-hand section-nav sidebar surface (Advanced Search). */
  navBackground: string;
  /** "SECTIONS" label above the Advanced Search nav. */
  navSectionLabelColor: string;
  /** Idle nav-item text. */
  navItemColor: string;
  /** Idle nav-item hover fill. */
  navItemHoverBackground: string;
  /** Active nav-item text. */
  navItemActiveColor: string;
  /** Active nav-item hover fill. */
  navItemActiveHoverBackground: string;
  /** Dark header-bar fill. Independent of the app chrome `headerBackground`. */
  headerBackground: string;
  /** Title color on the header bar. */
  headerTextColor: string;
  /** Leading title-icon color on the header bar. */
  headerIconColor: string;
  /** Close-button glyph color on the header bar. */
  headerCloseIconColor: string;
  /** Close-button hover fill on the header bar. */
  headerCloseHoverBackground: string;
  /** Subtitle color on the header bar. */
  headerSubtitleColor: string;
  /** Section heading in the Advanced Search body (e.g. "Identifiers"). */
  sectionTitleColor: string;
  /** Vertical accent bar beside an Advanced Search section heading. */
  sectionAccentColor: string;
  /** Label on an Advanced Search filter-list row (e.g. "Federal Tax Status"). */
  listRowLabelColor: string;
  /** Idle outlined toggle text on a filter-list row. */
  listToggleTextColor: string;
  /** Idle outlined toggle border on a filter-list row. */
  listToggleBorderColor: string;
  /** Hover fill on an idle outlined toggle; distinct from the row hover so it stays visible. */
  listToggleHoverBackground: string;
  /** Selected fill for a success-accent toggle (e.g. Tax Exempt). */
  listToggleSuccessBackground: string;
  /** Selected fill for an error-accent toggle (e.g. Taxable). */
  listToggleErrorBackground: string;
  /** Drop shadow under a selected error-accent toggle. */
  listToggleErrorShadow: string;
  /** Label color on a selected (contained) toggle. */
  listToggleSelectedTextColor: string;
  /** Accent text inside dialog bodies/footers (e.g. the "Save as:" label). */
  accentTextColor: string;
  /** Destructive icon (trash) on saved-search rows. */
  deleteIconColor: string;
  /** Label on the filled destructive confirm button (e.g. Delete). */
  destructiveButtonTextColor: string;
}

/**
 * Header account menu (the dropdown under the avatar in AppHeader). A distinct
 * surface from `dialog`: the app header bar stays dark in both themes, but the
 * menu popover follows the active theme (light in light mode, dark in dark).
 */
export interface AccountMenuPalette {
  /** Menu popover (Paper) surface. */
  background: string;
  /** Profile summary row background at the top of the menu. */
  headerBackground: string;
  /** Profile summary row hover fill. */
  headerHoverBackground: string;
  /** 1px section separators drawn on the item rows. */
  dividerColor: string;
  /** Item label text + profile summary primary line. */
  primaryTextColor: string;
  /** Profile summary secondary line (firm name / email). */
  secondaryTextColor: string;
  /** Leading item icons (Settings, Dark Mode, System Status). */
  iconColor: string;
  /** Demo Mode flask icon (SystemAdmin only). */
  demoIconColor: string;
  /** Off-state track fill for the menu toggle switches. */
  switchTrackColor: string;
  /** Logout row text + icon. */
  logoutColor: string;
}

/** Make Corrections dialog — colors specific to the corrections form. */
export interface MakeCorrectionsDialogPalette {
  /** Muted color for the "(optional)" hint next to a label. */
  optionalLabelColor: string;
  /** Input text color for corrected fields. */
  inputTextColor: string;
  /** Border/background accent when value is unchanged (parsed from source). */
  unchangedAccentColor: string;
  /** Border/background accent when value was modified by the user. */
  changedAccentColor: string;
  /** "Make Corrections" edit pencil icon color. */
  editIconColor: string;
}

/**
 * Advanced Search "Identifiers" inputs (Cusip / Issuer / Obligor) — a distinct
 * surface from the default filter inputs. Scoped exclusively to those three
 * inputs via `inputSx`; not consumed anywhere else.
 */
export interface IdentifierChipPalette {
  validText: string;
  validBackground: string;
  invalidText: string;
  invalidBackground: string;
}

export interface IdentifierInputPalette {
  backgroundColor: string;
  textColor: string;
  placeholderColor: string;
  /** Field outline (Cusip / Issuer / Obligor). */
  borderColor: string;
  /** Row label to the left of the field (Cusip / Issuer / Obligor). */
  labelColor: string;
  /** Suggestion-list option text (Obligor dropdown). */
  suggestionTextColor: string;
  /** Colors for the value chips rendered inside the identifier inputs. */
  chip: IdentifierChipPalette;
}

/**
 * Advanced Search Numeric Filters — row labels, Min/Max captions, number
 * inputs, and the compact checkboxes in that section (and Attributes).
 */
export interface NumericInputPalette {
  /** Row labels, Min/Max captions, unit suffixes, checkbox labels. */
  labelColor: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  placeholderColor: string;
  /** Checkbox glyph (idle + checked). */
  checkboxColor: string;
  /** Text inside unit/mode Select dropdowns (State, Workout Dates, etc.). */
  selectTextColor: string;
  /** Dropdown chevron inside unit/mode Select dropdowns. */
  selectIconColor: string;
}

/**
 * Tab-style toggle inside filter popovers (e.g. the Maturity mode selector).
 * Underlined tabs: the active tab colors its text and bottom border, inactive
 * tabs are muted; a full-width hairline sits under the row.
 */
export interface FilterToggleTabPalette {
  activeColor: string;
  inactiveColor: string;
  /** 1px rule under the tab row (inactive stretch; active tab overlays with its underline). */
  dividerColor: string;
}

/** Filter popover — label, input, and wrapper colors. */
export interface FilterPalette {
  labelColor: string;
  inputTextColor: string;
  inputPlaceholderColor: string;
  inputBackgroundColor: string;
  wrapperBorderColor: string;
  wrapperBackgroundColor: string;
  /** Colors for the Advanced Search identifier inputs only. */
  identifierInput: IdentifierInputPalette;
  /** Colors for the Advanced Search Numeric Filters inputs / labels. */
  numericInput: NumericInputPalette;
  /** Colors for the tab-style toggle (e.g. Maturity mode selector). */
  toggleTab: FilterToggleTabPalette;
}

/** Email-volume chart series inks (Message Metrics). */
export interface MessageMetricsChartPalette {
  /** Daily received bars. */
  dailyBar: string;
  /** Cumulative running-total line. */
  cumulativeLine: string;
  /** Dashed monthly-cap reference line. */
  limitLine: string;
  /** X-axis line ink. */
  axisLine: string;
}

/**
 * Reporting-period toggle inks, as opacities of `filter.toggleTab.activeColor`.
 * The ink itself is shared with the filter tabs; only how strongly it is
 * applied differs between the light and dark surfaces.
 */
export interface MessageMetricsTogglePalette {
  /** Outline around the group and between its tabs. */
  borderOpacity: number;
  /** Fill behind the selected period. */
  selectedOpacity: number;
  /** Fill behind the selected period while hovered. */
  selectedHoverOpacity: number;
  /** Fill behind an unselected period while hovered. */
  hoverOpacity: number;
}

/** Message Metrics panels and the email-volume chart series. */
export interface MessageMetricsPalette {
  /** Panel shadow; "none" in dark, where the card is delineated by a border instead. */
  cardShadow: string;
  /** Panel outline; "none" in light, where the shadow delineates the card instead. */
  cardBorder: string;
  /**
   * How strongly the period total's quota tone (success/warning/error) tints
   * its surface. A colour cannot be pre-resolved here because which of the
   * three applies is only known once the caps are read.
   */
  toneFillOpacity: number;
  /** Page/scroll-container background behind the metric cards. */
  pageBackground: string;
  /** Column-header text in the Sources table (Sender / Emails). */
  sourcesHeaderText: string;
  periodToggle: MessageMetricsTogglePalette;
  chart: MessageMetricsChartPalette;
}

/** Chart chrome for AI Assist / agent payload charts (axis, grid, series inks). */
export interface AiAssistChartPalette {
  /** Axis tick + label ink. */
  axis: string;
  /** Grid line + bottom axis line. */
  grid: string;
  /** Section/title ink above charts. */
  title: string;
  /** Hollow mark centre fill (matches the chart surface). */
  markFill: string;
  /** Background for markdown `<pre>` chart wrappers. */
  preBg: string;
  /** Primary series hues for pinned yield / compare mocks and in-chat curves. */
  series: readonly string[];
}

/** Pinned "Bond Detail" mock cards in the AI Assist artifacts panel. */
export interface AiAssistBondCardPalette {
  border: string;
  rowBorder: string;
  statsBg: string;
  headerBg: string;
  headerText: string;
  cusip: string;
  value: string;
  label: string;
  /** YTW / BUY positive accent. */
  positive: string;
  qtyValue: string;
  ratingBg: string;
  ratingText: string;
  stateBg: string;
  stateText: string;
  taxBg: string;
  taxText: string;
  buyBg: string;
  buyBorder: string;
}

/** AI Assist surface (page, sidebar, drawer, composer, panels) color tokens. */
export interface AiAssistPalette {
  // Surfaces
  pageBg: string;
  sidebarBg: string;
  cardBg: string;
  raisedBg: string;
  headerBg: string;
  inputBg: string;
  avatarBg: string;
  border: string;
  borderSubtle: string;
  // Text
  textTitle: string;
  textBody: string;
  textSecondary: string;
  textMuted: string;
  textFaint: string;
  textHint: string;
  textStrong: string;
  textConversation: string;
  onAccent: string;
  // Accents
  accent: string;
  accentWash: string;
  accentStrong: string;
  userBubbleBg: string;
  kindBlue: string;
  kindGreen: string;
  kindAmber: string;
  kindPurple: string;
  /** Negative/critical accent (e.g. thumbs-down feedback), matching the app red. */
  kindRed: string;
  gradientStart: string;
  gradientEnd: string;
  meterGradientStart: string;
  meterGradientEnd: string;
  fabBadgeBg: string;
  // Shadows / selection washes (theme-adaptive; avoid hardcoded rgba in components)
  /** Assistant FAB drop shadow (glow). */
  fabShadow: string;
  /** Assistant FAB drop shadow on hover. */
  fabShadowHover: string;
  /** FAB unread badge shadow. */
  fabBadgeShadow: string;
  /** Primary CTA button shadow (e.g. "New Conversation"). */
  buttonShadow: string;
  /** Primary CTA button shadow on hover. */
  buttonShadowHover: string;
  /** Selected conversation row background. */
  selectedBg: string;
  /** Selected conversation row background on hover. */
  selectedBgHover: string;
  /** Composer wrapper shadow (idle). */
  composerShadow: string;
  /** Composer wrapper shadow when active. */
  composerActiveShadow: string;
  /** Assistant message bubble shadow. */
  bubbleShadow: string;
  chart: AiAssistChartPalette;
  bondCard: AiAssistBondCardPalette;
}

/** App-specific theme keys (header link styles, etc.). */
export interface AppThemeStyles {
  headerLogo: CSSProperties;
  headerBackground: string;
  /** Scoped to header nav tabs only. */
  headerNavLinks: HeaderNavLinksPalette;
  /** Header search bar (outlined input). */
  headerSearch: HeaderSearchPalette;
  /** DataGrid color tokens. */
  dataGrid: DataGridPalette;
  /** Filter bar surfaces (Markets / Watchlists toolbar). */
  filterBar: FilterBarPalette;
  /** Filter popover colors (labels, inputs, wrapper). */
  filter: FilterPalette;
  /** App dialog (AppModal) colors. */
  dialog: DialogPalette;
  /** Header account-menu (avatar dropdown) colors. */
  accountMenu: AccountMenuPalette;
  /** Make Corrections dialog colors. */
  makeCorrectionsDialog: MakeCorrectionsDialogPalette;
  /** Message Metrics panel and chart colors. */
  messageMetrics: MessageMetricsPalette;
  /** AI Assist / agent surface colors. */
  aiAssist: AiAssistPalette;
}

declare module "@mui/material/styles" {
  interface Theme {
    app: AppThemeStyles;
  }
  interface ThemeOptions {
    app?: Partial<AppThemeStyles>;
  }
}

/** Serializable palette colors for a theme (subset of MUI PaletteOptions). */
export interface ThemePaletteColors {
  mode: "light" | "dark";
  primary: { main: string };
  secondary: { main: string };
  error: { main: string };
  background: { default: string; paper: string };
}

/** Serializable color part of the `theme.app` tokens (per-theme). */
export interface AppColorTokens {
  headerLogoColor: string;
  headerBackground: string;
  headerNavLinks: HeaderNavLinksPalette;
  headerSearch: HeaderSearchColors;
  dataGrid: DataGridPalette;
  filterBar: FilterBarPalette;
  filter: FilterPalette;
  dialog: DialogPalette;
  accountMenu: AccountMenuPalette;
  makeCorrectionsDialog: MakeCorrectionsDialogPalette;
  messageMetrics: MessageMetricsPalette;
  aiAssist: AiAssistPalette;
}

/**
 * Serializable theme definition — colors only; structure (typography, shape,
 * component overrides, sizes) lives in `baseTheme.ts` and is merged in by
 * `createAppTheme`. Used for both built-in and future API-loaded custom themes.
 */
export interface ThemeDefinition {
  id: string;
  name: string;
  builtIn: boolean;
  palette: ThemePaletteColors;
  appColors: AppColorTokens;
}

export { lightTheme, darkTheme };

export const BUILT_IN_THEMES: ThemeDefinition[] = [lightTheme, darkTheme];

/** Resolve a ThemeDefinition into a full MUI Theme: shared base + theme colors. */
export function createAppTheme(definition: ThemeDefinition): Theme {
  const { appColors } = definition;
  const app: AppThemeStyles = {
    headerLogo: { ...BASE_APP_LAYOUT.headerLogo, color: appColors.headerLogoColor },
    headerBackground: appColors.headerBackground,
    headerNavLinks: appColors.headerNavLinks,
    headerSearch: { ...BASE_APP_LAYOUT.headerSearch, ...appColors.headerSearch },
    dataGrid: appColors.dataGrid,
    filterBar: appColors.filterBar,
    filter: appColors.filter,
    dialog: appColors.dialog,
    accountMenu: appColors.accountMenu,
    makeCorrectionsDialog: appColors.makeCorrectionsDialog,
    messageMetrics: appColors.messageMetrics,
    aiAssist: appColors.aiAssist,
  };
  const options: ThemeOptions = {
    ...BASE_THEME_OPTIONS,
    palette: definition.palette,
    app,
  };
  return createTheme(options);
}

/**
 * Find a theme by ID in the given list, falling back to the light theme.
 * @see docs/adr/0002-fe-architecture-and-organization.md
 */
export function resolveTheme(
  themeId: string,
  allThemes: ThemeDefinition[],
): Theme {
  const def = allThemes.find((t) => t.id === themeId) ?? lightTheme;
  return createAppTheme(def);
}
